import React from 'react';
import { Outlet, Link, useLocation, useNavigation } from 'react-router-dom';
import { BannerPlaceholder, LanguageToggle, RouteLoadingProgress, ThemeToggle } from './shared/ui-react-compat';
import { useI18n } from './i18n';
import { NavLink, useAuthAwareLinks } from '@weretradeit/nav';
import { BKB_SHOP_URL } from './config/bkbShop';
import { assetUrl } from './utils/assetUrl';

// `@weretradeit/nav` kennt nur Router-Ziele. `external` markiert Eintraege auf
// einer anderen Domain: sie werden ein normales <a>. Als <Link to="/shop"> hat
// der Shop-Eintrag bis Oktober 2026 clientseitig die alte Platzhalterseite
// dieser SPA gerendert statt den echten Shop zu oeffnen.
type ShellNavLink = NavLink & { external?: boolean };

interface ThemeWindow extends Window {
  wtCookies?: { prefs?: { analytics?: boolean } };
  gtag?: (...args: any[]) => void;
}

const ShellLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, lang, setLang } = useI18n();
  const location = useLocation();
  // ui-react 4.0.0 (DS-6): the loading ramp needs the router state; without `active` it never showed.
  const navigation = useNavigation();

  React.useEffect(() => {
    // SPA Pageview tracking for GA4 (respects GDPR cookie consent)
    const w = window as ThemeWindow;
    if (typeof window !== 'undefined' && w.wtCookies?.prefs?.analytics && typeof w.gtag === 'function') {
      // Small timeout ensures the title has updated after route change
      setTimeout(() => {
        w.gtag?.('event', 'page_view', {
          page_path: location.pathname + location.search,
          page_location: window.location.href,
          page_title: document.title,
        });
      }, 100);
    }
  }, [location.pathname, location.search]);

  const baseNavLinks = React.useMemo<ShellNavLink[]>(() => [
    { to: '/', label: t('nav.home', 'Home') },
    { to: '/about', label: t('nav.about', 'Wer sind wir?') },
    { to: BKB_SHOP_URL, label: t('nav.shop', 'Shop'), external: true },
    { to: '/login', label: t('nav.login', 'Login'), audience: 'anon' },
    { to: '/impressum', label: t('nav.imprint', 'Impressum') },
    { to: '/datenschutz', label: t('nav.privacy', 'Datenschutz') },
  ], [t]);
  const { links } = useAuthAwareLinks(baseNavLinks);
  // Der Hook filtert nur nach Publikum und gibt dieselben Objekte zurueck;
  // `external` ist also noch da, auch wenn sein Rueckgabetyp es nicht kennt.
  const navLinks = links as ShellNavLink[];

  // Brand links (highlighted with colored borders). Die Bilder sind 20 px hoch:
  // das `icon` aus design-system 4.3 (brand/logos), nicht das ganze Logo —
  // unter 64 px tragen Zeichen und Leiterbahnen nicht.
  const brandLinks = React.useMemo(() => [
    { to: '/wt', label: t('nav.weretradeIT', 'weretradeIT'), brand: 'wt', logo: '/brand/logos/weretradeit/icon-light.svg' },
    { to: '/bkb', label: t('nav.bauKlotzBude', 'BauKlotzBude'), brand: 'bkb', logo: '/brand/logos/bauklotzbude/icon-light.svg' },
  ], [t]);

  return (
    <div className="app-layout app-shell">
      <RouteLoadingProgress active={navigation.state !== 'idle'} />
      <div className="top-banner-global">
        <BannerPlaceholder
          size="small"
          pill="Banner Slot"
          title="Top Banner 1100×160 · Full Width"
          note="Viewports: Desktop 1200×160, Tablet 900×140, Mobile 640×120"
          className="full-bleed"
        />
      </div>
      <nav className="top-nav">
        <div className="nav-links">
          {navLinks.map((link) => (link.external
            ? <a key={link.to} href={link.to}>{link.label}</a>
            : <Link key={link.to} to={link.to}>{link.label}</Link>
          ))}
        </div>
        <div className="nav-brands">
          {brandLinks.map((link) => (
            <Link key={link.to} to={link.to} className={`nav-brand-btn nav-brand-${link.brand}`}>
              {link.logo && <img src={assetUrl(link.logo)} alt={`${link.label} logo`} />}
              {link.label}
            </Link>
          ))}
        </div>
        <div className="nav-actions">
          {/* 0.6.x liess sich den Hook hineinreichen und hielt seinen Zustand
              selbst; 3.x ist KONTROLLIERT (value/onChange). Der Unterschied ist
              nicht nur die Form: die alte Fassung schrieb still in ihren eigenen
              localStorage-Schluessel, waehrend die App ihren eigenen fuehrte —
              zwei Quellen fuer eine Einstellung. Jetzt ist es die der App. */}
          <LanguageToggle value={lang} onChange={setLang} />
          {/* Beide Umschalter tragen jetzt `.segmented` aus dem Design-System.
              Vorher kam der Theme-Knopf aus einem Vanilla-Widget mit eigenem
              Stil — siehe den Kommentar in index.html. */}
          <ThemeToggle />
        </div>
      </nav>
      {/* id ist das Ziel der Sprungmarke aus index.html; tabIndex={-1} sorgt dafuer,
          dass der Fokus nach dem Sprung wirklich hier landet und nicht nur die
          Bildlaufposition springt. */}
      <main id="main-content" tabIndex={-1}>{children}</main>
    </div>
  );
};

const App = () => (
  <ShellLayout>
    <Outlet />
  </ShellLayout>
);

export default App;
